import { type BeforeLeaveEventArgs, useBeforeLeave } from "@solidjs/router";
import { type Accessor, createSignal } from "solid-js";

type UseLeaveConfirmationOptions = {
  /** Show the dialog when this returns true. */
  when: Accessor<boolean>;
  /** Called after the user confirms leaving. Use it to clear state before navigation resumes. */
  onConfirm: () => void;
  /** Route substrings that bypass the guard (e.g. ["send-proposal", "new-proposal"]). */
  excludedRoutes?: string[];
};

type UseLeaveConfirmationReturn = {
  pendingLeave: Accessor<BeforeLeaveEventArgs | null>;
  handleLeaveConfirm: () => void;
  handleLeaveCancel: () => void;
};

export function useLeaveConfirmation(
  options: UseLeaveConfirmationOptions,
): UseLeaveConfirmationReturn {
  const [pendingLeave, setPendingLeave] =
    createSignal<BeforeLeaveEventArgs | null>(null);

  useBeforeLeave((e) => {
    const toPath =
      typeof e.to === "string" ? e.to.split("?")[0].split("#")[0] : "";
    const isExcluded = (options.excludedRoutes ?? []).some((route) =>
      new RegExp(`(^|/)${route}(/|$)`).test(toPath),
    );

    if (options.when() && !e.defaultPrevented && !isExcluded) {
      e.preventDefault();
      setPendingLeave(e);
    }
  });

  const handleLeaveConfirm = () => {
    const e = pendingLeave();
    setPendingLeave(null);
    options.onConfirm();
    e?.retry(true);
  };

  const handleLeaveCancel = () => {
    setPendingLeave(null);
  };

  return { pendingLeave, handleLeaveConfirm, handleLeaveCancel };
}
